import type {
  ShellBridge,
  ShellUiConfig,
  ShellEventMap,
  ShellState,
  Dispose,
  Auth0State,
  ShellAuth0Bridge,
  GetTokenSilentlyOptions,
  GetTokenSilentlyVerboseResponse,
  IdToken,
  DrawerSlotOptions,
  DrawerSlotHandle,
  OverlaySlotOptions,
  OverlaySlotHandle,
} from '@opengov/psp-contract';
import type { ShellBridgeLike } from '@opengov/analytics';
import type { ModuleAnalytics } from '../analytics/ogAnalytics';
import type { ShellBridgeStore } from './ShellBridgeStore';
import type { ShellEventBus } from './ShellEventBus';
import type { Logger } from '../../types';
import { headerActionSlotRef } from './headerActionSlot';
import { drawerSlotStore } from '../drawerSlots/drawerSlotStore';
import { overlaySlotStore } from '../overlaySlots/overlaySlotStore';

/**
 * `analytics` is not part of `@opengov/psp-contract`'s `ShellBridge`. The SDK
 * types it structurally (`ShellBridgeLike`), and modules read it through the
 * SDK's `OgAnalyticsProvider mode="shell"`, so the contract does not need to
 * depend on the analytics library.
 */
type ShellBridgeWithAnalytics = ShellBridge & ShellBridgeLike;

interface ShellBridgeResult {
  bridge: ShellBridgeWithAnalytics;
  disposeAll: Dispose;
}

/**
 * Implementation-side interface — the shell injects this into the bridge
 * factory so `bridge.auth0` can forward calls back into the shell's single
 * Auth0 client. Kept narrow on purpose: each method maps 1:1 to a single
 * `useAuth()` field, which keeps the boundary stable as PSP's auth hook
 * evolves.
 */
export interface Auth0Provider {
  getAuth0State(): Auth0State;
  getAccessTokenSilently: {
    (
      options: GetTokenSilentlyOptions & { detailedResponse: true }
    ): Promise<GetTokenSilentlyVerboseResponse>;
    (options?: GetTokenSilentlyOptions): Promise<string>;
  };
  getIdTokenClaims(): Promise<IdToken | undefined>;
  requestLogout(): Promise<void>;
}

/**
 * Implementation-side interface for `bridge.navigateToDeepLink` — the shell
 * injects this so the bridge can resolve a deep link via PSP's own app
 * registry and perform the actual in-shell navigation (React Router or
 * iframe state), exactly like PSP's own TaskBar/TaskDrawer do. Optional at
 * the `createShellBridge` call site: callers that don't supply one (e.g.
 * unauthenticated/global app shells) simply don't get `navigateToDeepLink`
 * on their bridge instance.
 */
export interface NavigationProvider {
  navigateToDeepLink(deeplinkUrl: string, subject: string | null | undefined): void;
}

/**
 * Implementation-side interface for `bridge.setActiveEntity` — the shell injects
 * this so the bridge can switch the active entity via the shell's EntityContext
 * (`setActiveEntityByFriendlyId`). Optional at the `createShellBridge` call site:
 * callers that don't supply one simply don't get `setActiveEntity` on their bridge.
 */
export interface EntityProvider {
  setActiveEntity(friendlyId: string): void;
}

/**
 * Creates a per-app shell bridge instance.
 * The bridge is a plain JS object that remote apps use to communicate with the shell.
 * All registrations are tracked so they can be auto-cleaned on unmount.
 */
export function createShellBridge(
  appId: string,
  store: ShellBridgeStore,
  getShellState: () => ShellState,
  eventBus: ShellEventBus,
  auth0Provider: Auth0Provider,
  navigationProvider?: NavigationProvider,
  entityProvider?: EntityProvider,
  /** Optional — used for remoteNav adoption telemetry (presence only). */
  logger?: Pick<Logger, 'info'>,
  /** Optional — this module's analytics scope, exposed as `shell.analytics`.
   *  Absent when the build has no analytics key, so modules feature-detect. */
  moduleAnalytics?: ModuleAnalytics
): ShellBridgeResult {
  const disposables = new Set<Dispose>();
  let isDisposed = false;
  let currentContributionDispose: Dispose | null = null;
  /** Once per bridge lifetime — adoption signal, not per re-sync. */
  let hasLoggedRemoteNavContribution = false;

  const auth0: ShellAuth0Bridge = {
    getAuth0State: () => auth0Provider.getAuth0State(),

    onAuth0StateChange(handler): Dispose {
      if (isDisposed) {
        console.warn(`[ShellBridge] App "${appId}" called onAuth0StateChange after dispose`);
        return () => {};
      }
      const dispose = eventBus.on('auth0Change', handler);
      disposables.add(dispose);
      return () => {
        dispose();
        disposables.delete(dispose);
      };
    },

    // Forward the function reference directly so its overload signature is
    // preserved end-to-end (string vs verbose-response).
    // TODO(logger): on a 4xx error, log via the bridge logger once available.
    getAccessTokenSilently: auth0Provider.getAccessTokenSilently,

    getIdTokenClaims: () => auth0Provider.getIdTokenClaims(),

    // TODO(logger): log logout requests via bridge logger once available.
    requestLogout: () => auth0Provider.requestLogout(),
  };

  // Retired with the rest of the bridge, so an unmounted module cannot keep
  // sending events under the signed-in identity.
  if (moduleAnalytics) disposables.add(moduleAnalytics.dispose);

  const bridge: ShellBridgeWithAnalytics = {
    version: 1,
    auth0,
    analytics: moduleAnalytics?.bridge,

    setUiContributions(config: ShellUiConfig): Dispose {
      if (isDisposed) {
        console.warn(`[ShellBridge] App "${appId}" called setUiContributions after dispose`);
        return () => {};
      }

      // Drop the previous disposer from the tracking set without invoking it.
      // store.setContributions overwrites by appId, so clearing first would emit a
      // spurious "empty" snapshot and cause a double re-render in consumers.
      if (currentContributionDispose) {
        disposables.delete(currentContributionDispose);
      }

      // Adoption telemetry: log once when this app first shares a remoteNav config.
      // Presence only — never log config values (can include auth blobs / PII).
      if (config.remoteNav && !hasLoggedRemoteNavContribution) {
        hasLoggedRemoteNavContribution = true;
        try {
          logger?.info('Remote app contributed remoteNav', { appId });
        } catch {
          // Adoption telemetry is best-effort; never break the bridge call.
        }
      }

      store.setContributions(appId, config);

      const dispose: Dispose = () => {
        // Only clear if this is still the active registration — a later setUiContributions
        // call may have replaced us, in which case clearing would wipe the newer state.
        if (currentContributionDispose === dispose) {
          store.clearContributions(appId);
          currentContributionDispose = null;
        }
        disposables.delete(dispose);
      };

      currentContributionDispose = dispose;
      disposables.add(dispose);
      return dispose;
    },

    subscribe<K extends keyof ShellEventMap>(event: K, handler: ShellEventMap[K]): Dispose {
      if (isDisposed) {
        console.warn(`[ShellBridge] App "${appId}" called subscribe after dispose`);
        return () => {};
      }

      const dispose = eventBus.on(event, handler);
      disposables.add(dispose);
      return () => {
        dispose();
        disposables.delete(dispose);
      };
    },

    getState(): ShellState {
      return getShellState();
    },

    requestHeaderSlot(): HTMLElement | null {
      return headerActionSlotRef.current;
    },

    requestDrawerSlot(options?: DrawerSlotOptions): DrawerSlotHandle | null {
      if (isDisposed) {
        console.warn(`[ShellBridge] App "${appId}" called requestDrawerSlot after dispose`);
        return null;
      }
      // Modules always land in the `remote` tier — to the right of any
      // shell-managed drawer. The `order` hint only sorts among module drawers.
      const handle = drawerSlotStore.register({ tier: 'remote', order: options?.order });
      disposables.add(handle.release);
      return handle;
    },

    closeShellDrawers(): void {
      if (isDisposed) {
        console.warn(`[ShellBridge] App "${appId}" called closeShellDrawers after dispose`);
        return;
      }
      // Closes shell-managed drawers (e.g. OG Assist) so a module's own
      // right-side panel can take the space. Module (`remote`) drawers are
      // untouched.
      drawerSlotStore.closeManagedDrawers();
    },

    requestOverlaySlot(options: OverlaySlotOptions): OverlaySlotHandle | null {
      if (isDisposed) {
        console.warn(`[ShellBridge] App "${appId}" called requestOverlaySlot after dispose`);
        return null;
      }
      const handle = overlaySlotStore.claim({
        ariaLabel: options.ariaLabel,
        onRequestClose: options.onRequestClose,
      });
      disposables.add(handle.release);
      return handle;
    },

    closeShellOverlay(): void {
      if (isDisposed) {
        console.warn(`[ShellBridge] App "${appId}" called closeShellOverlay after dispose`);
        return;
      }
      overlaySlotStore.close();
    },

    ...(navigationProvider
      ? {
          navigateToDeepLink(deeplinkUrl: string, subject?: string | null): void {
            if (isDisposed) {
              console.warn(`[ShellBridge] App "${appId}" called navigateToDeepLink after dispose`);
              return;
            }
            navigationProvider.navigateToDeepLink(deeplinkUrl, subject);
          },
        }
      : {}),

    ...(entityProvider
      ? {
          setActiveEntity(friendlyId: string): void {
            if (isDisposed) {
              console.warn(`[ShellBridge] App "${appId}" called setActiveEntity after dispose`);
              return;
            }
            entityProvider.setActiveEntity(friendlyId);
          },
        }
      : {}),
  };

  function disposeAll(): void {
    if (isDisposed) return;
    isDisposed = true;
    for (const dispose of disposables) {
      try {
        dispose();
      } catch (err) {
        console.error(`[ShellBridge] Dispose failed for app "${appId}":`, err);
      }
    }
    disposables.clear();
    currentContributionDispose = null;
  }

  return { bridge, disposeAll };
}
